import { Box, Card, Typography } from '@mui/material'
import React, { useContext } from 'react'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DisabledOverlay, OfflineOverlay } from '../Overlays'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { NewLabel, useHasNewFeature } from 'src/Libs/new-content'
import { useNavigate } from 'react-router-dom'
import { SystemSettingsDataCardModel } from 'src/Libs/system-settings/types'

const useStyles = makeStyles()(theme => ({
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
      minHeight: '200px',
    },
    borderRadius: theme.shape.borderRadius,
    borderColor: theme.palette.primary.main,
    padding: theme.spacing(3),
    cursor: 'pointer',
    transition: theme.transitions.create('all'),
    '@media (hover: hover)': {
      '&:hover': {
        transition: theme.transitions.create('all'),
        border: `1px solid ${theme.palette.primary.main}`,
        transform: 'scale(1.02)',
      },
      '&:hover div': {
        transition: theme.transitions.create('all'),
      },
    },
  },
  bodyContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  iconContainer: {
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  footer: {
    color: theme.palette.primary.main,
    paddingTop: theme.spacing(2),
  },
}))

type CardProps = SystemSettingsDataCardModel

const SystemSettingCard: React.FC<CardProps> = ({
  icon,
  title,
  description,
  path,
  hasNewContent,
  elementKey,
  accessibleWhenInstallationOffline,
  disabled,
  ...rest
}) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { isStorageOnline } = useContext(InstallationOnlineStatusContext)
  const { handleUpdateContentVersion } = useHasNewFeature()
  const navigate = useNavigate()
  const showNewLabel = hasNewContent && isStorageOnline

  const handleOnClick = (): void => {
    navigate(path)
    handleUpdateContentVersion([elementKey])
  }

  return (
    <OfflineOverlay isOffline={!isStorageOnline && !accessibleWhenInstallationOffline}>
      <DisabledOverlay isDisabled={disabled}>
        <Card className={classes.cardContainer} {...rest} onClick={handleOnClick}>
          {showNewLabel && <NewLabel sx={{ position: 'absolute', top: 12, right: 12 }} />}
          <Box className={classes.bodyContainer}>
            <Box className={classes.iconContainer}>{icon}</Box>
            <Typography variant={'h3'} className={classes.title}>
              {title}
            </Typography>
            <Typography variant={'subtitle2'}>{description}</Typography>
          </Box>
          <Box className={classes.footer}>
            <Typography variant={'subtitle2'}>{t('common.explore')}</Typography>
          </Box>
        </Card>
      </DisabledOverlay>
    </OfflineOverlay>
  )
}

export default SystemSettingCard
